import React, { Fragment, Suspense, useMemo } from "react";

import { css } from "@compiled/react";

import type { ExtensionHandlers } from "@atlaskit/editor-common/extensions";
import { type RendererProps } from "@atlaskit/renderer";
import { token } from "@atlaskit/tokens";

import { Button, Link } from "@/libraries/design/components";
import { LogoV3 } from "@/libraries/wpl/foundations/src";

import { Source } from "../ai-summary";
import { useAISummaryContext } from "../ai-summary-context";

import { encodeAdf } from "./encoder";

const AkReactRenderer = React.lazy(() =>
    import("@atlaskit/renderer").then(({ ReactRenderer }) => ({
        default: ReactRenderer
    }))
);

export type MarkdownRendererProps = {
    answerString: string;
    statusComponent: (props: {
        text?: string;
        sources?: Source[];
    }) => React.ReactNode;
    query?: string;
};

const MediaComponent = () => <Fragment />;
const MediaSingleComponent = () => <Fragment />;

const buttonWrapperStyle = css({
    display: "inline-block",
    marginBlockStart: token("space.100"),
    marginInlineEnd: token("space.100")
});

const rovoButtonWrapperStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    marginBlockStart: token("space.200")
});

const rovoButtonStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    maxWidth: "none",
    height: "20px"
});

const getRovoChatUrl = (query: string) => {
    const chatUrl = new URL("https://home.atlassian.com/chat");
    chatUrl.searchParams.set("rovoChatPathway", "chat");
    chatUrl.searchParams.set("rovoChatPrompt", query);

    const loginUrl = `https://id.atlassian.com/login?continue=${encodeURIComponent(
        chatUrl.toString()
    )}`;

    return loginUrl;
};

const RovoButton = ({ text, href }: { text: string; href: string }) => (
    <Button
        variant="secondary"
        colorAgainst="white"
        css={rovoButtonStyle}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
    >
        <LogoV3
            brand="rovo"
            type="mark"
            appearance="brand"
            width={20}
            height={20}
        />
        <span>{text}</span>
    </Button>
);

const ButtonComponent = ({
    text,
    buttonType,
    href
}: {
    text: string;
    buttonType: string;
    href: string;
}) => {
    if (buttonType === "navigation") {
        return (
            <Link
                href={href}
                type="primary"
                aria-label={`${text}button`}
                text={text}
            />
        );
    }

    if (buttonType === "rovo") {
        return <RovoButton text={text} href={href} />;
    }

    return (
        <Button
            variant="secondary"
            colorAgainst="white"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
        >
            {text}
        </Button>
    );
};

const createCtaButtonExtensionHandler = (
    isRovoEnabledCohort: boolean,
    query?: string
): ExtensionHandlers => ({
    "com.atlassian.wac": (ext) => {
        const { extensionKey, parameters } = ext;

        if (extensionKey === "cta-button" && parameters) {
            const { text, buttonType, href } = parameters as {
                text: string;
                buttonType: string;
                href: string;
            };

            const isRovoButton = buttonType === "rovo";
            const resolvedText =
                isRovoButton && isRovoEnabledCohort
                    ? "Send this to Rovo"
                    : text;
            const resolvedHref =
                isRovoButton && isRovoEnabledCohort
                    ? getRovoChatUrl(query || "")
                    : href;

            // Wrap in span to ensure inline rendering
            return (
                <div
                    css={[
                        buttonWrapperStyle,
                        isRovoButton && rovoButtonWrapperStyle
                    ]}
                >
                    <ButtonComponent
                        text={resolvedText}
                        buttonType={buttonType}
                        href={resolvedHref}
                    />
                </div>
            );
        }

        return null;
    }
});

// Extension handler for CTA buttons
// Handles inlineExtension nodes with extensionKey: "cta-button"
export const MarkdownRenderer = ({
    answerString,
    statusComponent,
    query
}: MarkdownRendererProps) => {
    const { isRovoEnabledCohort } = useAISummaryContext();

    const docContent = useMemo(
        () => encodeAdf(answerString, false),
        [answerString]
    );

    const ctaButtonExtensionHandler = useMemo(
        () =>
            createCtaButtonExtensionHandler(
                Boolean(isRovoEnabledCohort),
                query
            ),
        [isRovoEnabledCohort, query]
    );

    const config: RendererProps = {
        // @ts-expect-error - DocNode type unavailable, fallback to unknown
        document: docContent as unknown,
        nodeComponents: {
            status: statusComponent,
            media: MediaComponent,
            mediaSingle: MediaSingleComponent
        },
        extensionHandlers: ctaButtonExtensionHandler,
        smartLinks: {
            ssr: true
        }
    };

    return (
        <Suspense fallback={<Fragment />}>
            <AkReactRenderer {...config} />
        </Suspense>
    );
};
